import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useI18n } from '@oneaudi/i18n-context';
import { Icon, iconSystemInfoSmall } from './icon';
import { AudiPlatformProvider } from '@audi/audi-ui-react-v2';
export const RenderedMarker = ({ picture }) => {
    const popupButtonRef = useRef(null);
    const [popupButtonBottom, setPopupButtonBottom] = useState(0);
    const [popupButtonLeft, setPopupButtonLeft] = useState(0);
    const [open, setOpen] = useState(false);
    const popupContent = useI18n({
        id: 'nemo.ui.sc.stage.renderedDisclaimer',
        defaultMessage: '',
    });
    const renderImageAriaLabel = useI18n({
        id: 'stockcars.images.renderimages.arialabel',
        defaultMessage: '',
    });
    const handleScroll = () => {
        setOpen(false);
    };
    useEffect(() => {
        window.removeEventListener('scroll', handleScroll);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);
    const clickHandler = (evt) => {
        evt.preventDefault();
        const popupButtonCoord = popupButtonRef.current.getBoundingClientRect();
        if (popupButtonCoord) {
            setPopupButtonBottom(popupButtonCoord.bottom + document.documentElement.scrollTop);
            setPopupButtonLeft(popupButtonCoord.left);
        }
        setOpen(true);
        if (evt.type === 'click')
            setTimeout(() => setOpen(false), 4000);
    };
    if (!picture || !isTypedPicture(picture) || picture.type !== 'render') {
        return null;
    }
    if (!popupContent.trim()) {
        return null;
    }
    return (React.createElement(PopupContainer, null,
        React.createElement(PopupButton, { type: "button", "aria-label": renderImageAriaLabel, onClick: clickHandler, "data-testid": "infoBtn", onMouseEnter: clickHandler, onMouseLeave: () => {
                setOpen(false);
            }, ref: popupButtonRef },
            React.createElement(Icon, { icon: iconSystemInfoSmall })),
        React.createElement(React.Fragment, null, open &&
            ReactDOM.createPortal(React.createElement(AudiPlatformProvider, null,
                React.createElement(PopupContent, { role: "tooltip", id: picture === null || picture === void 0 ? void 0 : picture.key, top: popupButtonBottom, left: popupButtonLeft }, popupContent)), document.body))));
};
const PopupContainer = styled.div `
  position: absolute;
  top: var(${(props) => { var _a, _b; return (_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.responsive) === null || _b === void 0 ? void 0 : _b.spacing.s; }});
  left: var(${(props) => { var _a, _b; return (_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.responsive) === null || _b === void 0 ? void 0 : _b.spacing.s; }});
`;
const PopupContent = styled.div `
  display: block;
  background-color: var(${(props) => { var _a, _b; return (_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.colors) === null || _b === void 0 ? void 0 : _b.base.grey[0]; }});
  position: absolute;
  left: ${(props) => props.left - 10.5}px;
  top: ${(props) => props.top + 7}px;
  box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.3);
  width: 18vw;
  max-width: 320px;
  word-wrap: break-word;
  z-index: 999;
  padding: var(${(props) => { var _a, _b; return (_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.responsive) === null || _b === void 0 ? void 0 : _b.spacing.s; }});
  font-family: var(${(props) => { var _a; return (_a = props.theme) === null || _a === void 0 ? void 0 : _a.fontFamily; }});
  font-size: var(${(props) => { var _a, _b; return (_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.responsive) === null || _b === void 0 ? void 0 : _b.typography.copy1.fontSize; }});
  color: var(${(props) => { var _a, _b; return (_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.colors) === null || _b === void 0 ? void 0 : _b.base.grey[80]; }});
  font-weight: var(${(props) => { var _a, _b; return (_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.fontWeights) === null || _b === void 0 ? void 0 : _b.normal; }});
  font-stretch: var(${(props) => { var _a, _b; return (_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.responsive) === null || _b === void 0 ? void 0 : _b.typography.copy1.fontStretch; }});
  text-align: left;
  line-height: var(${(props) => { var _a, _b; return (_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.responsive) === null || _b === void 0 ? void 0 : _b.typography.copy1.lineHeight; }});
  -webkit-font-smoothing: antialiased;

  &::before {
    content: '';
    position: absolute;
    top: -8px;
    left: 13px;
    width: 18px;
    height: 8px;
    background-color: var(${(props) => { var _a, _b; return (_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.colors) === null || _b === void 0 ? void 0 : _b.base.grey[0]; }});
    clip-path: polygon(0 100%, 50% 0, 100% 100%);
  }
  @media (max-width: ${(props) => { var _a, _b; return (_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.breakpoints) === null || _b === void 0 ? void 0 : _b.m; }}px) {
    width: 315px;
  }
`;
const PopupButton = styled.button `
  position: absolute;
  margin: 0;
  padding: 0;
  background: none;
  border: 0;
  color: var(${(props) => { var _a, _b; return (_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.colors) === null || _b === void 0 ? void 0 : _b.base.grey[100]; }});
  cursor: pointer;
  z-index: 2;

  svg {
    display: block;
  }

  path {
    fill: var(${(props) => { var _a, _b; return (_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.colors) === null || _b === void 0 ? void 0 : _b.base.grey[0]; }});
  }
`;
export function isTypedPicture(picture) {
    return 'type' in picture;
}
